import { useState } from "react";
import { toast } from "sonner";
import { Bell, BellRing, Check } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS } from "../lib/bookingStatus";

/**
 * Shared Telegram-Push-Setup Karte — funktioniert für Team-Mitglieder UND
 * Kunden. Backend-Endpoints sind rollen-neutral:
 *   • PATCH /me/preferences   → speichert `telegram_chat_id`
 *   • POST  /me/telegram/test → sendet einen Test-Push an die eigene Chat-ID
 *
 * `variant` steuert nur die Kopie/den Ton der Karte:
 *   • "team"     → Push für dringende Ankünfte
 *   • "customer" → Push für automatische Willkommens-Nachricht + Updates
 */
export default function TelegramPushCard({ variant = "customer" }) {
  const { lang } = useI18n();
  const { user, refresh } = useAuth();
  const [chatId, setChatId] = useState(user?.telegram_chat_id || "");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const active = !!user?.telegram_chat_id;
  const isCustomer = variant === "customer";
  // Team keeps German; customer follows language toggle.
  const de = !isCustomer || lang !== "en";

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/preferences", { telegram_chat_id: chatId.trim() || null });
      await refresh?.();
      toast.success(chatId ? (de ? "Chat-ID gespeichert." : "Chat ID saved.") : (de ? "Chat-ID entfernt." : "Chat ID removed."));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post("/me/telegram/test");
      toast.success(de ? "Test-Push gesendet — bitte Telegram öffnen." : "Test push sent — please open Telegram.");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || (de ? "Fehler beim Senden." : "Sending failed.")); }
    finally { setTesting(false); }
  };

  return (
    <div className="bg-white border border-navy/10 p-6 md:p-7" data-testid="telegram-push-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="min-w-0">
          <div className="label-caps mb-2 flex items-center gap-1.5">
            {active ? <BellRing className="w-3 h-3" strokeWidth={2} style={{ color: BRAND_COLORS.gold }} /> : <Bell className="w-3 h-3" strokeWidth={1.6} />}
            Push · Telegram
          </div>
          <h2 className="font-display text-2xl text-navy">
            {isCustomer ? (de ? "Auf Telegram benachrichtigen" : "Notifications on Telegram") : "Dringende Ankünfte"}
          </h2>
          <p className="text-[13px] text-navy-dark/70 mt-2 max-w-xl">
            {isCustomer
              ? (de
                ? (<>Erhalten Sie 24 Stunden vor Ihrer Ankunft eine kurze Willkommensnachricht auf Telegram und bleiben Sie über wichtige Änderungen an Ihrem Zuhause informiert, auch wenn die App gerade nicht offen ist. Bitte verstehen Sie, dass dies keine Chatfunktion ist, sondern eine Kommunikationsart, damit Sie App-Benachrichtigungen empfangen ohne die App öffnen zu müssen. <strong className="font-semibold text-navy">Bei dringendem Bedarf können Sie hier natürlich eine Nachricht hinterlassen oder anrufen.</strong></>)
                : (<>Receive a short welcome message on Telegram 24 hours before your arrival and stay informed about important changes to your home, even when the app is not open. Please understand that this is not a chat feature, but a way of communicating so you can receive app notifications without having to open the app. <strong className="font-semibold text-navy">If you urgently need something, you are of course welcome to leave a message here or call.</strong></>))
              : "Sie erhalten sofort eine Telegram-Nachricht, wenn ein Kunde eine Ankunft innerhalb der nächsten 48 Stunden anfragt. So können Sie die Vorbereitung ohne Verzögerung planen."}
          </p>
        </div>
        {active && (
          <span
            className="inline-flex items-center gap-2 px-3 py-1 text-[10.5px] tracking-[0.2em] uppercase border rounded-full shrink-0"
            style={{ borderColor: "#2E5D4E40", color: BRAND_COLORS.greenDeep }}
            data-testid="telegram-active-pill"
          >
            <Check className="w-3 h-3" strokeWidth={2.6} />{de ? "Aktiv" : "Active"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3">
        <input
          data-testid="telegram-chatid-input"
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          placeholder={de ? "Ihre Telegram-Chat-ID (z. B. 959862760)" : "Your Telegram chat ID (e.g. 959862760)"}
          className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[14px]"
        />
        <button
          data-testid="telegram-save-btn"
          onClick={save}
          disabled={saving}
          className="text-ivory px-5 py-3 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
          style={{ background: BRAND_COLORS.navy }}
        >
          {saving ? "…" : (de ? "Speichern" : "Save")}
        </button>
        <button
          data-testid="telegram-test-btn"
          onClick={test}
          disabled={testing || !active}
          className="border border-gold text-navy px-5 py-3 text-[11px] tracking-[0.15em] uppercase hover:bg-gold/10 disabled:opacity-40"
          title={active ? (de ? "Test-Nachricht an Ihre Chat-ID senden" : "Send test message to your chat ID") : (de ? "Erst Chat-ID speichern" : "Save chat ID first")}
        >
          {testing ? (de ? "Sende…" : "Sending…") : (de ? "Test-Nachricht" : "Test message")}
        </button>
      </div>
      <details className="mt-4 text-[12px] text-navy-dark/70" data-testid="telegram-howto">
        <summary className="cursor-pointer hover:text-navy select-none">
          {de ? "So finden Sie Ihre Chat-ID (2 Min.)" : "How to find your chat ID (2 min)"}
        </summary>
        <ol className="mt-3 pl-5 space-y-1 list-decimal">
          <li>{de ? "Öffnen Sie " : "Open "}<a href="https://t.me/Weissblom_concierge_bot" target="_blank" rel="noreferrer" className="text-navy border-b border-gold">@Weissblom_concierge_bot</a>{de ? " in Telegram" : " on Telegram"}</li>
          <li>{de
            ? <>Tippen Sie <strong>START</strong> und schreiben Sie irgendetwas — z. B. „Hallo".</>
            : <>Tap <strong>START</strong> and send anything — e.g. \u201CHi".</>}</li>
          <li>{de
            ? <>Bitten Sie Ihr Weissblom-Team, Ihre Chat-ID aus dem Bot-Log zu übermitteln, oder rufen Sie <code className="bg-navy/5 px-1 break-all">api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> im Browser auf.</>
            : <>Ask your Weissblom team to share your chat ID from the bot log, or open <code className="bg-navy/5 px-1 break-all">api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> in a browser.</>}</li>
          <li>{de
            ? <>Kopieren Sie den <code className="bg-navy/5 px-1">chat.id</code>-Wert (eine Zahl) und speichern Sie ihn hier oben.</>
            : <>Copy the <code className="bg-navy/5 px-1">chat.id</code> value (a number) and save it above.</>}</li>
          <li>{de
            ? <>Zum Verifizieren auf <strong>Test-Nachricht</strong> klicken — Telegram muss innerhalb weniger Sekunden pingen.</>
            : <>To verify, tap <strong>Test message</strong> — Telegram should ping within a few seconds.</>}</li>
        </ol>
        <p className="mt-3 text-navy-dark/55 italic">
          {de
            ? "Datenschutz: Weissblom nutzt Ihre Chat-ID ausschließlich für automatische Willkommens-Nachrichten und dringende Statusmeldungen zu Ihrem Objekt. Sie können die Verknüpfung jederzeit durch Leeren des Feldes und Speichern entfernen."
            : "Privacy: Weissblom uses your chat ID only for automatic welcome messages and urgent status notifications about your property. You can remove the link at any time by clearing the field and saving."}
        </p>
      </details>
    </div>
  );
}
